// ─────────────────────────────────────────────────────────────────────────────
// Backup Storage Integration Service
// Handles blob storage operations and automatically updates backup metadata
// Ensures URLs are stored instead of blob data for sync operations
// ─────────────────────────────────────────────────────────────────────────────

import { BlobStorageService, BlobUploadResult } from './blobStorageService';
import { 
  saveBackupMetadata, 
  getBackupMetadataBySedationId, 
  updateSyncStatus,
  generateBackupChecksum,
  BackupMetadataRecord,
  backupMetadataRepo 
} from '../hooks/repos/backupMetadataRepo';
import { CompleteSedationBackup } from '../types/backupTypes';

export interface BackupUploadResult {
  success: boolean;
  backupMetadataId?: string;
  pdfUrl?: string;
  imageUrls?: string[];
  dataBackupUrl?: string;
  error?: string;
}

export interface BackupAssets {
  pdfBlob?: Blob;
  pdfFilename?: string;
  imageBlobs?: Array<{ blob: Blob; hourNumber: number; index: number }>;
  sedationData?: CompleteSedationBackup;
}

export class BackupStorageIntegrationService {
  private blobStorageService: BlobStorageService;

  constructor(blobStorageService: BlobStorageService) {
    this.blobStorageService = blobStorageService;
  }

  /**
   * Upload all backup assets and create/update backup metadata
   * This is the main method to use - it handles everything automatically
   */
  async uploadBackupAssets(
    patientId: string,
    sedationId: string,
    patientName: string,
    assets: BackupAssets
  ): Promise<BackupUploadResult> {
    try {
      // Initialize tracking variables
      let pdfUrl: string | null = null;
      const imageUrls: string[] = [];
      let dataBackupUrl: string | null = null;
      let uploadErrors: string[] = [];

      // 1. Upload PDF if provided
      if (assets.pdfBlob) {
        const pdfResult = await this.blobStorageService.uploadPdfReport(
          patientId, 
          sedationId, 
          assets.pdfBlob,
          assets.pdfFilename
        );
        
        if (pdfResult.success && pdfResult.url) {
          pdfUrl = pdfResult.url;
        } else {
          uploadErrors.push(`PDF upload failed: ${pdfResult.error}`);
        }
      }

      // 2. Upload images if provided
      if (assets.imageBlobs && assets.imageBlobs.length > 0) {
        const batchResult = await this.blobStorageService.uploadSedationImagesBatch(
          patientId,
          sedationId,
          assets.imageBlobs
        );
        
        // Collect successful image URLs
        batchResult.results.forEach(result => {
          if (result.success && result.url) {
            imageUrls.push(result.url);
          } else {
            uploadErrors.push(`Image upload failed: ${result.error}`);
          }
        });
      }

      // 3. Upload sedation data backup if provided
      if (assets.sedationData) {
        // CRITICAL: Remove any blob data from sedationData before uploading
        const cleanedSedationData = this.cleanBlobDataFromPayload(assets.sedationData);
        
        const dataResult = await this.blobStorageService.uploadSedationDataBackup(
          patientId,
          sedationId,
          cleanedSedationData
        );
        
        if (dataResult.success && dataResult.url) {
          dataBackupUrl = dataResult.url;
        } else {
          uploadErrors.push(`Data backup upload failed: ${dataResult.error}`);
        }
      }

      // 4. Create or update backup metadata record
      const backupMetadata = await this.createOrUpdateBackupMetadata(
        patientId,
        sedationId,
        patientName,
        {
          pdfUrl,
          patientPackUrl: null, // Backend will populate this
          imageUrls,
          dataBackupUrl,
          assets
        }
      );

      // 5. Determine overall success
      const success = uploadErrors.length === 0;
      
      return {
        success,
        backupMetadataId: backupMetadata.Id,
        pdfUrl: pdfUrl || undefined,
        imageUrls: imageUrls.length > 0 ? imageUrls : undefined,
        dataBackupUrl: dataBackupUrl || undefined,
        error: uploadErrors.length > 0 ? uploadErrors.join('; ') : undefined
      };

    } catch (error) {
      console.error('❌ Critical error during backup upload:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown backup error'
      };
    }
  }

  /**
   * Update existing backup metadata with new URLs
   */
  async updateBackupUrls(
    sedationId: string,
    urls: {
      pdfUrl?: string;
      patientPackUrl?: string;
      imageUrls?: string[];
      dataBackupUrl?: string;
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const existingRecord = await getBackupMetadataBySedationId(sedationId);
      if (!existingRecord) {
        return { success: false, error: `No backup metadata found for sedation ${sedationId}` };
      }

      const updateData: Partial<BackupMetadataRecord> = {};

      if (urls.pdfUrl) {
        updateData.PdfUrl = urls.pdfUrl;
        updateData.HasReport = true;
      }

      if (urls.patientPackUrl) {
        updateData.PatientPackUrl = urls.patientPackUrl;
      }

      if (urls.imageUrls && urls.imageUrls.length > 0) {
        // Merge with existing image URLs, avoiding duplicates
        const existingUrls = existingRecord.ImageUrls || [];
        const allUrls = [...existingUrls, ...urls.imageUrls];
        updateData.ImageUrls = [...new Set(allUrls)]; // Remove duplicates
        updateData.ImagesCount = updateData.ImageUrls.length;
      }

      if (urls.dataBackupUrl) {
        updateData.DataBackupUrl = urls.dataBackupUrl;
        updateData.HasCompletePatientData = true;
        updateData.HasCompleteSedationDetail = true;
      }

      await backupMetadataRepo.update(existingRecord.Id, updateData);
      return { success: true };

    } catch (error) {
      console.error('❌ Failed to update backup URLs:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown update error' 
      };
    }
  }

  /**
   * Get sync-ready payload with URLs instead of blob data
   */
  async getSyncReadyPayload(sedationId: string): Promise<{
    success: boolean;
    payload?: CompleteSedationBackup & { backupMetadata?: BackupMetadataRecord };
    error?: string;
  }> {
    try {
      const backupMetadata = await getBackupMetadataBySedationId(sedationId);
      if (!backupMetadata) {
        return { 
          success: false, 
          error: `No backup metadata found for sedation ${sedationId}` 
        };
      }

      if (!backupMetadata.DataBackupUrl) {
        return { 
          success: false, 
          error: `No data backup URL available for sedation ${sedationId}` 
        };
      }

      // Download the clean sedation data from blob storage
      const downloadResult = await this.blobStorageService.downloadBlob(
        this.extractBlobNameFromUrl(backupMetadata.DataBackupUrl),
        'data'
      );

      if (!downloadResult.success || !downloadResult.data) {
        return { 
          success: false, 
          error: `Failed to download backup data: ${downloadResult.error}` 
        };
      }

      // Parse the clean sedation data
      const cleanPayload = JSON.parse(await downloadResult.data.text()) as CompleteSedationBackup;

      return {
        success: true,
        payload: {
          ...cleanPayload,
          backupMetadata // Include backup metadata for reference
        }
      };

    } catch (error) {
      console.error('❌ Failed to get sync-ready payload:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Mark backup metadata as synced
   */
  async markBackupSynced(sedationId: string): Promise<void> {
    const record = await getBackupMetadataBySedationId(sedationId);
    if (record) {
      await updateSyncStatus(record.Id, 'synced');
    }
  }

  /**
   * Mark backup metadata as sync failed
   */
  async markBackupSyncFailed(sedationId: string, error: string): Promise<void> {
    const record = await getBackupMetadataBySedationId(sedationId);
    if (record) {
      await updateSyncStatus(record.Id, 'failed', error);
    }
  }

  // Private helper methods

  private async createOrUpdateBackupMetadata(
    patientId: string,
    sedationId: string,
    patientName: string,
    data: {
      pdfUrl: string | null;
      patientPackUrl: string | null;
      imageUrls: string[];
      dataBackupUrl: string | null;
      assets: BackupAssets;
    }
  ): Promise<BackupMetadataRecord> {
    // Check if record already exists
    const existingRecord = await getBackupMetadataBySedationId(sedationId);
    
    if (existingRecord) {
      // Update existing record
      const updateData: Partial<BackupMetadataRecord> = {
        PdfUrl: data.pdfUrl,
        PatientPackUrl: data.patientPackUrl,
        ImageUrls: [...(existingRecord.ImageUrls || []), ...data.imageUrls],
        DataBackupUrl: data.dataBackupUrl,
        ImagesCount: data.imageUrls.length + (existingRecord.ImagesCount || 0),
        HasReport: !!data.pdfUrl,
        SyncStatus: 'pending' as const
      };

      await backupMetadataRepo.update(existingRecord.Id, updateData);
      return await getBackupMetadataBySedationId(sedationId) as BackupMetadataRecord;
    } else {
      // Create new record
      const backupData = {
        PatientId: patientId,
        SedationId: sedationId,
        PatientName: patientName,
        BackupVersion: "1.0",
        GeneratedAt: new Date().toISOString(),
        BackupType: 'complete' as const,
        
        // URLs from uploads
        PdfUrl: data.pdfUrl,
        PatientPackUrl: data.patientPackUrl,
        ImageUrls: data.imageUrls,
        DataBackupUrl: data.dataBackupUrl,
        
        // Statistics
        MeasurementsCount: this.countMeasurements(data.assets.sedationData),
        ImagesCount: data.imageUrls.length,
        SessionDataSize: this.calculateDataSize(data.assets.sedationData),
        
        // Data integrity flags
        HasCompletePatientData: !!data.assets.sedationData?.Patient,
        HasCompleteSedationDetail: !!data.assets.sedationData?.SedationDetail,
        HasIntraOpData: this.hasIntraOpData(data.assets.sedationData),
        HasMeasurements: this.hasMeasurements(data.assets.sedationData),
        HasReport: !!data.pdfUrl,
        
        // Sync status
        SyncStatus: 'pending' as const,
        SyncAttempts: 0,
        LastSyncAttempt: null,
        SyncError: null,
        
        // Base entity fields
        ModifiedDateTime: new Date().toISOString(),
        Synced: false,
        Deactivated: false,
        LastSyncedAt: null,
        PendingSync: 'create' as const,
        
        // Checksum
        Checksum: generateBackupChecksum({
          patientId,
          sedationId,
          urls: data,
          timestamp: Date.now()
        })
      };

      return await saveBackupMetadata(backupData);
    }
  }

  /**
   * Remove blob data from payload to prevent API failures
   * CRITICAL: This ensures no blob data is sent to APIs
   */
  private cleanBlobDataFromPayload(data: CompleteSedationBackup): CompleteSedationBackup {
    const cleanData = JSON.parse(JSON.stringify(data)); // Deep clone
    
    // Remove any blob data fields that might cause API failures
    const removeBlobs = (obj: any): any => {
      if (!obj || typeof obj !== 'object') return obj;
      
      if (obj instanceof Blob || obj instanceof File) {
        return null; // Remove blob data
      }
      
      // Handle arrays
      if (Array.isArray(obj)) {
        return obj.map(removeBlobs).filter(item => item !== null);
      }
      
      // Handle objects
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        // Skip known blob fields
        if (key.toLowerCase().includes('blob') || 
            key.toLowerCase().includes('file') ||
            key.toLowerCase() === 'data' && value instanceof Blob) {
          continue;
        }
        cleaned[key] = removeBlobs(value);
      }
      
      return cleaned;
    };
    
    return removeBlobs(cleanData);
  }

  private extractBlobNameFromUrl(url: string): string {
    // Extract blob name from Azure blob URL
    const urlParts = url.split('/');
    return urlParts[urlParts.length - 1].split('?')[0]; // Remove query params
  }

  private countMeasurements(sedationData?: CompleteSedationBackup): number {
    return sedationData?.SedationMeasurements?.length || 0;
  }

  private calculateDataSize(sedationData?: CompleteSedationBackup): number {
    return sedationData ? JSON.stringify(sedationData).length : 0;
  }

  private hasIntraOpData(sedationData?: CompleteSedationBackup): boolean {
    return !!(sedationData?.IntraOppState);
  }

  private hasMeasurements(sedationData?: CompleteSedationBackup): boolean {
    return !!(sedationData?.SedationMeasurements && sedationData.SedationMeasurements.length > 0);
  }

  /**
   * Check if backup assets already exist for a sedation to prevent duplicate uploads
   * Returns existing URLs if found, null if no backup exists
   */
  async checkExistingBackup(
    sedationId: string
  ): Promise<{ 
    exists: boolean; 
    urls?: { pdfUrl?: string; imageUrls?: string[]; dataBackupUrl?: string } 
  }> {
    try {
      const existingMetadata = await getBackupMetadataBySedationId(sedationId);
      
      if (existingMetadata) {
        return {
          exists: true,
          urls: {
            pdfUrl: existingMetadata.PdfUrl || undefined,
            imageUrls: existingMetadata.ImageUrls || [],
            dataBackupUrl: existingMetadata.DataBackupUrl || undefined
          }
        };
      }
      
      return { exists: false };
    } catch (error) {
      console.warn('⚠️ Error checking existing backup:', error);
      return { exists: false };
    }
  }

  /**
   * Smart upload that only uploads assets that don't already exist
   * This prevents duplicate uploads and saves bandwidth/storage
   */
  async smartUploadBackupAssets(
    patientId: string,
    sedationId: string,
    patientName: string,
    assets: BackupAssets,
    forceReupload: boolean = false
  ): Promise<BackupUploadResult> {
    try {
      // Check if we already have backup URLs (unless forced to re-upload)
      if (!forceReupload) {
        const existingCheck = await this.checkExistingBackup(sedationId);
        
        if (existingCheck.exists && existingCheck.urls) {
          const { pdfUrl, imageUrls, dataBackupUrl } = existingCheck.urls;
          
          // If we have all the URLs we need, return them without re-uploading
          const hasPdf = pdfUrl && assets.pdfBlob;
          const hasImages = imageUrls && imageUrls.length > 0 && assets.imageBlobs?.length;
          const hasData = dataBackupUrl && assets.sedationData;
          
          // Only skip if we have URLs for all provided assets
          const shouldSkipUpload = (!assets.pdfBlob || hasPdf) && 
                                   (!assets.imageBlobs?.length || hasImages) && 
                                   (!assets.sedationData || hasData);
          
          if (shouldSkipUpload) {
           
            return {
              success: true,
              pdfUrl: pdfUrl || undefined,
              imageUrls: imageUrls || [],
              dataBackupUrl: dataBackupUrl || undefined,
              backupMetadataId: await getBackupMetadataBySedationId(sedationId)?.then(r => r?.Id)
            };
          }
        }
      }
      
      // Proceed with normal upload if no existing backup or forced re-upload
      return await this.uploadBackupAssets(patientId, sedationId, patientName, assets);
      
    } catch (error) {
      console.error('❌ Smart upload failed, falling back to normal upload:', error);
      return await this.uploadBackupAssets(patientId, sedationId, patientName, assets);
    }
  }
}

// Export singleton instance factory
export const createBackupStorageService = (blobStorageService: BlobStorageService) => {
  return new BackupStorageIntegrationService(blobStorageService);
};
